/**
 * Cones BOGO tier selector.
 *
 * One state object drives both the tier cards and the mix-scents grid, so the
 * two controls can never drift apart. Tier cards are presets that write into
 * that state rather than a parallel mechanism.
 *
 *   state.singles = { '<scent name>': qty }   // qty of the single-box variant
 *   state.pack    = 0 | 1                     // qty of the multi-box variant
 *
 * The multi-box variant is deliberately NOT part of `singles`: it is a
 * different variant and does not participate in the BOGO discount.
 */
if (!window.ConesBogo) {
  class ConesBogo {
    constructor(root) {
      this.root = root;

      try {
        this.config = JSON.parse(root.querySelector('[data-cones-bogo-config]').textContent);
      } catch (error) {
        console.warn('[cones-bogo] invalid config, selector disabled', error); // eslint-disable-line
        return;
      }

      this.form = document.querySelector('.js-product-form-main');
      if (!this.form) return;

      this.scentNames = this.config.scents.map((scent) => scent.name);
      this.state = { singles: {}, pack: 0 };
      this.mode = 'tier';
      this.tier = 2;

      // A six-pack deep link (?variant=...) should land on tier 3. Anything
      // else — including a bare product URL — keeps the tier 2 default.
      if (this.getSelectedSize() === this.config.packSizeValue) {
        this.tier = 3;
      }

      this.applyTier(this.tier, { syncSize: false });
      this.bind();
      this.render();
    }

    /* ----------------------------------------------------------- helpers */

    scent(name) {
      return this.config.scents.find((entry) => entry.name === name) || this.config.scents[0];
    }

    currentScent() {
      const selected = this.getSelectedOption(this.config.scentOption);
      return selected || this.config.currentScent;
    }

    /**
     * Returns the fieldset for a named product option in the variant picker.
     * @param {string} optionName - The option's name.
     * @returns {Element|null}
     */
    optionSelector(optionName) {
      return document.querySelector(
        `variant-picker .option-selector[data-option="${CSS.escape(optionName)}"]`
      );
    }

    getSelectedOption(optionName) {
      const selector = this.optionSelector(optionName);
      if (!selector) return null;
      const checked = selector.querySelector('.opt-btn:checked');
      if (checked) return checked.value;
      const native = selector.querySelector('select');
      return native ? native.value : null;
    }

    getSelectedSize() {
      return this.getSelectedOption(this.config.sizeOption);
    }

    /**
     * Selects a value in the (visually hidden) size option, letting the
     * theme's own variant picker resolve the variant, price and media.
     * @param {string} value - The option value to select.
     */
    setSize(value) {
      if (this.getSelectedSize() === value) return;

      const selector = this.optionSelector(this.config.sizeOption);
      if (!selector) return;

      const input = Array.from(selector.querySelectorAll('.opt-btn')).find(
        (opt) => opt.value === value
      );

      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const native = selector.querySelector('select');
      if (native) {
        native.value = value;
        native.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* ------------------------------------------------------------- state */

    totalSingles() {
      return Object.values(this.state.singles).reduce((sum, qty) => sum + qty, 0);
    }

    /**
     * Free-box count, payable count and money total for the current singles.
     * The discount is uncapped, so every complete group yields another free
     * box. Shopify discounts the cheapest eligible units, so the same rule is
     * used here to keep the displayed total honest.
     * @returns {object}
     */
    derive() {
      const group = this.config.bogoGroup;
      const quantity = this.totalSingles();
      const free = Math.floor(quantity / group);
      const payable = quantity - free;

      const units = [];
      Object.entries(this.state.singles).forEach(([name, qty]) => {
        for (let i = 0; i < qty; i += 1) units.push(this.scent(name).single.price);
      });
      units.sort((a, b) => a - b);

      const gross = units.reduce((sum, price) => sum + price, 0);
      const freeValue = units.slice(0, free).reduce((sum, price) => sum + price, 0);

      return {
        quantity,
        free,
        payable,
        gross,
        total: gross - freeValue,
        toNext: quantity === 0 ? group : (group - (quantity % group)) % group
      };
    }

    /**
     * Writes a tier preset into the shared state.
     * @param {number} tier - Tier 1, 2 or 3.
     * @param {object} [opts] - Options.
     * @param {boolean} [opts.syncSize=true] - Also select the matching size option.
     */
    applyTier(tier, opts = {}) {
      const { syncSize = true } = opts;
      const scentName = this.currentScent();

      this.tier = tier;
      this.mode = 'tier';
      this.state.singles = {};
      this.state.pack = 0;

      if (tier === 3) {
        this.state.pack = 1;
        if (syncSize) this.setSize(this.config.packSizeValue);
      } else {
        this.state.singles[scentName] = tier === 2 ? this.config.bogoGroup : 1;
        if (syncSize) this.setSize(this.config.singleSizeValue);
      }
    }

    /**
     * Derives which tier card (if any) matches the current mix, so the two
     * controls stay in sync when the shopper toggles between them.
     * @returns {number|null}
     */
    matchingTier() {
      if (this.state.pack === 1 && this.totalSingles() === 0) return 3;

      const active = Object.entries(this.state.singles).filter(([, qty]) => qty > 0);
      if (active.length !== 1) return null;

      const [, qty] = active[0];
      if (qty === 1) return 1;
      if (qty === this.config.bogoGroup) return 2;
      return null;
    }

    /* -------------------------------------------------------- cart items */

    /**
     * The line items the current state would add to the cart.
     * @returns {Array}
     */
    items() {
      if (this.state.pack === 1) {
        return [{ id: this.scent(this.currentScent()).pack.id, quantity: 1 }];
      }

      return Object.entries(this.state.singles)
        .filter(([, qty]) => qty > 0)
        .map(([name, qty]) => ({ id: this.scent(name).single.id, quantity: qty }));
    }

    /* ------------------------------------------------------------ events */

    bind() {
      this.root.addEventListener('click', this.handleClick.bind(this));

      // The variant picker replaces every [data-dynamic-variant-content]
      // region on change, which includes the hidden quantity input and the
      // add-to-cart button. Re-apply our state whenever that happens.
      document.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      // Capture phase on document, so this runs before <product-form>'s own
      // AT_TARGET submit listener and can take over for multi-variant adds.
      document.addEventListener('submit', this.handleSubmit.bind(this), true);

      // on:variant:change may fire either side of the DOM swap, so also watch
      // the form directly. renderButton and syncFormInputs only write when a
      // value actually differs, so this cannot feed back into itself.
      this.observer = new MutationObserver(() => {
        this.renderButton();
        this.syncFormInputs();
      });
      this.observer.observe(this.form, { childList: true, subtree: true });
    }

    /**
     * Keeps the form's hidden inputs matching the shared state after the
     * variant picker has replaced them.
     */
    syncFormInputs() {
      const items = this.items();
      if (items.length === 1) this.writeFormInputs(items[0]);
    }

    handleClick(evt) {
      const tierBtn = evt.target.closest('[data-cones-tier]');
      if (tierBtn) {
        this.applyTier(Number(tierBtn.dataset.conesTier));
        this.render();
        return;
      }

      const toggle = evt.target.closest('[data-cones-mix-toggle]');
      if (toggle) {
        const mix = this.root.querySelector('[data-cones-mix]');
        const open = mix.hasAttribute('hidden');
        mix.toggleAttribute('hidden', !open);
        toggle.setAttribute('aria-expanded', String(open));
        return;
      }

      const step = evt.target.closest('[data-cones-step]');
      if (step) {
        const row = step.closest('[data-cones-mix-row]');
        const name = row.dataset.conesMixRow;
        const next = (this.state.singles[name] || 0) + Number(step.dataset.conesStep);

        // Stepping up out of tier 3 abandons the pack and starts a mix.
        this.state.pack = 0;
        this.state.singles[name] = Math.max(0, next);
        this.mode = 'mix';
        this.tier = this.matchingTier();
        this.render();
      }
    }

    handleVariantChange() {
      const scentName = this.currentScent();

      // A tier preset follows the shopper's scent choice; an explicit mix
      // does not, because they chose those quantities deliberately.
      if (this.mode === 'tier' && this.tier && this.tier !== 3) {
        this.state.singles = {};
        this.state.singles[scentName] = this.tier === 2 ? this.config.bogoGroup : 1;
      }

      this.render();
    }

    async handleSubmit(evt) {
      if (evt.target !== this.form) return;

      const items = this.items();

      if (items.length === 0) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        return;
      }

      // Single-variant adds go through the theme's own <product-form>
      // handler, preserving its error handling, drawer behaviour and events.
      // We only make sure the form carries the right variant and quantity.
      if (items.length === 1) {
        this.writeFormInputs(items[0]);
        this.scheduleDrawerRepair();
        return;
      }

      evt.preventDefault();
      evt.stopImmediatePropagation();
      await this.addMultiple(items);
    }

    /**
     * Forces the product form's hidden inputs to match our state, so a fast
     * click cannot submit a stale variant while a picker fetch is in flight.
     * @param {object} item - Line item with id and quantity.
     */
    writeFormInputs(item) {
      const idInput = this.form.querySelector('input[name="id"]');
      const qtyInput = this.form.querySelector('[name="quantity"]');
      if (idInput && idInput.value !== String(item.id)) idInput.value = item.id;
      if (qtyInput && qtyInput.value !== String(item.quantity)) qtyInput.value = item.quantity;
    }

    /**
     * Safety net for the single-variant path, which is handled by the theme's
     * own product-form.js. Because cart POST endpoints render sections against
     * the PUBLISHED theme, an unpublished preview leaves the drawer blank.
     * Repairs only that exact symptom: a non-empty cart rendering zero lines.
     * In production the drawer is already correct and this is a no-op.
     */
    scheduleDrawerRepair() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;

      setTimeout(async () => {
        try {
          const response = await fetch(`${window.theme.routes.cart}.js`);
          const cart = await response.json();

          if (cart.item_count > 0 && cartDrawer.querySelectorAll('.cart-item').length === 0) {
            await cartDrawer.refresh(true);
            cartDrawer.open();
          }
        } catch (error) {
          console.log(error); // eslint-disable-line
        }
      }, 1500);
    }

    /**
     * Adds several variants in one request. Mirrors product-form.js so the
     * cart drawer updates through the theme's existing render path.
     * @param {Array} items - Line items to add.
     */
    async addMultiple(items) {
      const button = this.form.querySelector('[name="add"]');
      const errorEl = this.form.querySelector('.js-form-error');
      const cartDrawer = document.querySelector('cart-drawer');

      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('is-loading');
      }

      try {
        const sections = cartDrawer
          ? [...new Set(cartDrawer.getSectionsToRender().map((section) => section.section))]
          : [];

        const response = await fetch(window.theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript'
          },
          body: JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || data.message || response.status);
        }

        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }

        if (cartDrawer) {
          ConesBogo.renderDrawer(cartDrawer, data, true);
        } else {
          window.location.href = window.theme.routes.cart;
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        if (errorEl) {
          errorEl.textContent = error.message || window.theme.strings.cartError;
          errorEl.hidden = false;
        }
      } finally {
        if (button) {
          button.removeAttribute('aria-disabled');
          button.classList.remove('is-loading');
        }
      }
    }

    /**
     * Updates the cart drawer after an add.
     *
     * The cart POST endpoints render sections against the PUBLISHED theme, so
     * while previewing an unpublished theme the drawer's own section id comes
     * back null and renderContents would blank the drawer. Falling back to the
     * drawer's GET-based refresh keeps preview QA honest; in production the
     * POST sections are present and the normal path runs.
     * @param {Element} cartDrawer - The cart-drawer element.
     * @param {object} data - Response from /cart/add.
     * @param {boolean} openDrawer - Whether to open the drawer afterwards.
     */
    static async renderDrawer(cartDrawer, data, openDrawer) {
      const sectionId = cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');

      if (data.sections && data.sections[sectionId]) {
        cartDrawer.renderContents(data, openDrawer);
        return;
      }

      await cartDrawer.refresh(true);
      if (openDrawer) cartDrawer.open();
    }

    /* ------------------------------------------------------------ render */

    formatMoney(cents) {
      const format = this.config.moneyFormat || '${{amount}}';
      const value = Math.round(cents);

      const withDelimiters = (num, precision, thousands, decimal) => {
        const fixed = (num / 100).toFixed(precision);
        const [whole, fraction] = fixed.split('.');
        const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        return fraction ? `${grouped}${decimal}${fraction}` : grouped;
      };

      return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
        switch (name) {
          case 'amount_no_decimals':
            return withDelimiters(value, 0, ',', '.');
          case 'amount_with_comma_separator':
            return withDelimiters(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return withDelimiters(value, 0, '.', ',');
          default:
            return withDelimiters(value, 2, ',', '.');
        }
      });
    }

    render() {
      const scent = this.scent(this.currentScent());
      const group = this.config.bogoGroup;
      const single = scent.single.price;

      const tierValues = {
        1: { total: single, was: 0, unit: single },
        2: {
          total: single * (group - 1),
          was: single * group,
          unit: Math.round((single * (group - 1)) / group)
        },
        3: {
          total: scent.pack.price,
          was: scent.pack.compareAt > scent.pack.price ? scent.pack.compareAt : 0,
          unit: Math.round(scent.pack.price / this.config.packCount)
        }
      };

      // Highlight whichever tier the shared state currently equals, so the
      // cards and the mix grid can never disagree about what is selected.
      const selectedTier = this.matchingTier();

      this.root.querySelectorAll('[data-cones-tier]').forEach((card) => {
        const tier = Number(card.dataset.conesTier);
        const values = tierValues[tier];
        const selected = selectedTier === tier;

        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-checked', String(selected));

        card.querySelector('[data-cones-tier-total]').textContent = this.formatMoney(values.total);
        card.querySelector('[data-cones-tier-unit]').textContent = `${this.formatMoney(values.unit)} per box`;

        const was = card.querySelector('[data-cones-tier-was]');
        if (was) {
          was.hidden = values.was === 0;
          was.textContent = values.was === 0 ? '' : this.formatMoney(values.was);
        }
      });

      this.renderMix();
      this.renderButton();

      // The MutationObserver can fire while the picker is mid-swap, before
      // handleVariantChange has moved the state onto the newly chosen scent,
      // which would leave a stale variant id in the form. Re-sync here, after
      // the state is settled, so the DOM always matches the selection.
      this.syncFormInputs();
    }

    renderMix() {
      const { quantity, free, total, gross, toNext } = this.derive();
      const group = this.config.bogoGroup;

      this.root.querySelectorAll('[data-cones-mix-row]').forEach((row) => {
        const qty = this.state.singles[row.dataset.conesMixRow] || 0;
        row.querySelector('[data-cones-mix-value]').textContent = qty;
        row.querySelector('[data-cones-step="-1"]').disabled = qty === 0;
      });

      const status = this.root.querySelector('[data-cones-mix-status]');
      if (status) {
        const boxes = (count) => (count === 1 ? 'box' : 'boxes');

        if (quantity === 0) {
          status.textContent = `Add ${group} boxes and your ${group}rd is free.`;
        } else if (free === 0) {
          status.textContent = `Add ${toNext} more ${boxes(toNext)} and your ${group}rd is free.`;
        } else if (toNext === 0) {
          status.textContent =
            free === 1 ? 'Nice — 1 box is free.' : `Nice — ${free} boxes are free.`;
        } else {
          status.textContent = `${free} ${boxes(free)} free. Add ${toNext} more for another.`;
        }
      }

      const totalEl = this.root.querySelector('[data-cones-mix-total]');
      if (totalEl) totalEl.textContent = this.formatMoney(total);

      // Only strike a price through once a box is genuinely free. At one or
      // two boxes there is no discount and a struck price would be a lie
      // that checkout will not honour.
      const wasEl = this.root.querySelector('[data-cones-mix-was]');
      if (wasEl) {
        wasEl.hidden = free === 0;
        wasEl.textContent = free === 0 ? '' : this.formatMoney(gross);
      }
    }

    renderButton() {
      const button = this.form.querySelector('[name="add"]');
      if (!button) return;

      const { quantity, total } = this.derive();

      let label;
      let disabled = false;

      if (this.state.pack === 1) {
        const scent = this.scent(this.currentScent());
        label = `Add ${this.config.packCount} boxes to cart — ${this.formatMoney(scent.pack.price)}`;
      } else if (quantity === 0) {
        label = 'Choose a quantity';
        disabled = true;
      } else {
        label = `Add ${quantity} ${quantity === 1 ? 'box' : 'boxes'} to cart — ${this.formatMoney(total)}`;
      }

      if (button.textContent.trim() !== label) button.textContent = label;
      if (button.disabled !== disabled) button.disabled = disabled;
    }
  }

  window.ConesBogo = ConesBogo;

  const init = () => {
    document.querySelectorAll('[data-cones-bogo]').forEach((root) => {
      if (!root.dataset.conesBogoReady) {
        root.dataset.conesBogoReady = 'true';
        new ConesBogo(root); // eslint-disable-line no-new
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
